import { useState, useContext, useEffect } from "react";
import { Button } from "@mui/material";
import { Code, Monitor, Smartphone } from "lucide-react";
import { ScreenSizeContext } from "../../context/ScreenSizeContext";
import api from "../../services/api";
import { HtmlCodeExtractedContext } from "../../context/HtmlCodeExtracted";
import { SelectedElementContex } from "../../context/SelectedElement";
import TestEmailPopUp from "./TestEmailPopUp";
import { toast } from "react-toastify";
import { EmailTemplateContext } from "../../context/EmailTemplateContext";
import { useNavigate, useLocation } from "react-router-dom";
import { GetHtmlCodeContext } from "../../context/GetHtmlCodeContex";
import TemplatesDialog from "./TemplatesDialog";
import SaveOptionsDialog from "./SaveOptionsDialog";

const EmailEditorHeader = ({ viewHTMLCode, setCopy, setTestEmail }) => {
  const { screenSize, setScreenSize } = useContext(ScreenSizeContext);
  const { htmlCode, setSaveEmailTemplate } = useContext(
    HtmlCodeExtractedContext
  );
  const { getHtmlCode } = useContext(GetHtmlCodeContext);
  const { attachments, sendToCcBccSubject, emailTemplate } =
    useContext(EmailTemplateContext);
  const { setEditDiv, setSelectedElement } = useContext(SelectedElementContex);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showTemp, setShowTemp] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  // const location = useLocation();
  // const { query } = location.state || {};

  const handleClose = () => {
    setOpen(false);
  };

  function handleSuccessMessage(message) {
    toast.success(message, {
      autoClose: 2000,
      position: "top-center",
    });
  }

  function handleErrorMessage(message) {
    toast.error(message, {
      autoClose: 2000,
      position: "top-center",
    });
  }

  const sendEmail = async (email) => {
    setLoading(true);
    try {
      // Format attachments for backend
      const formattedAttachments = attachments.map((attachment) => ({
        name: attachment.name,
        link: attachment.link,
      }));
      const formattedSubject = sendToCcBccSubject.subjectFor

      const response = await api.post(
        "/schedule_email/send-email",
        {
          fromEmail: sendToCcBccSubject.fromEmail,
          senderName: sendToCcBccSubject.fromName,
          to: email,
          subject: formattedSubject,
          htmlTemplate: htmlCode,
          attachments:
            formattedAttachments.length > 0 ? formattedAttachments : null,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const res = await response.data;

      if (res.success) {
        setTestEmail(false);
        setOpen(false);
        handleSuccessMessage("Email sent successfully");
      } else {
        handleErrorMessage(res.message || "Failed to send email.");
      }
    } catch (err) {
      console.error("Error sending email: ", err);
      handleErrorMessage(err.response?.data?.detail || "Something went wrong!");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async (saveOption, scheduledTime=null ) => {
    setLoading(true);
    if (!getHtmlCode || typeof getHtmlCode !== "function") {
      console.error("getHtmlCode function not available");
      handleErrorMessage("Unable to generate HTML code. Please try again.");
      setLoading(false);
      return;
    }
    const htmlCodeFromFn = await getHtmlCode();

    if (!htmlCodeFromFn) {
      handleErrorMessage("Generated HTML Code is Empty.");
      setLoading(false);
      return;
    }
    if (!query) {
      handleErrorMessage("Please filter Data 1st!");
      return;
    }

      try {
      const response = await api.post("/schedule_email/save_email_temp", {
        htmlTemplate: htmlCodeFromFn,
        toCcBccSubject: sendToCcBccSubject,
        attachments: attachments,
        metaData: emailTemplate,
        saveOption: saveOption,
        query: query,
        scheduledTime: scheduledTime,
      });
      const res = response.data;
      if (res.success) {
        handleSuccessMessage("Template saved successfully.");
        navigate("/schedule_email");
        // localStorage.
      }
    } catch (err) {
      console.error("Error saving template: ", err);
      handleErrorMessage("Failed to save template.");
    } finally {
      setLoading(false);
      // setSaveEmailTemplate(false);
    }
  };

  // const handleSaveTemplate = async (saveData) => {
  //   setLoading(true);

  //   if (!getHtmlCode || typeof getHtmlCode !== "function") {
  //     console.error("getHtmlCode function not available");
  //     handleErrorMessage("Unable to generate HTML code. Please try again.");
  //     setLoading(false);
  //     return;
  //   }

  //   const htmlCodeFromFn = await getHtmlCode();

  //   if (!htmlCodeFromFn) {
  //     handleErrorMessage("Generated HTML Code is Empty.");
  //     setLoading(false);
  //     return;
  //   }

  //   try {
  //     const response = await api.post("/schedule_email/save_email_action", {
  //       action: saveData.action,
  //       htmlTemplate: htmlCodeFromFn,
  //       toCcBccSubject: sendToCcBccSubject,
  //       attachments: attachments,
  //       metaData: emailTemplate,
  //       columnValues: saveData.columnValues,
  //       scheduledTime: saveData.scheduledTime,
  //     });

  //     const res = response.data;
  //     if (res.success) {
  //       handleSuccessMessage(res.message);
  //       setShowSaveDialog(false);

  //       if (saveData.action !== "save") {
  //         // Navigate to email bucket/schedule page
  //         navigate("/schedule_email");
  //       }
  //     } else {
  //       handleErrorMessage(res.message || "Operation failed");
  //     }
  //   } catch (err) {
  //     console.error("Error saving template: ", err);
  //     handleErrorMessage(
  //       err.response?.data?.detail || "Failed to save template."
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Add handler in EmailEditorHeader
  const handleSelectTemplate = (template) => {
    try {
      // Parse the template data
      const toCcBcc =
        typeof template.toCcBccSubject === "string"
          ? JSON.parse(template.toCcBccSubject)
          : template.toCcBccSubject;

      const attachmentsData =
        typeof template.attachments === "string"
          ? JSON.parse(template.attachments)
          : template.attachments;

      const metadataData =
        typeof template.metadata === "string"
          ? JSON.parse(template.metadata)
          : template.metadata;

      // Update localStorage and refresh the page
      localStorage.setItem(
        "attachments",
        JSON.stringify(attachmentsData || [])
      );
      localStorage.setItem(
        "sendToCcBccSubject",
        JSON.stringify(
          toCcBcc || {
            sendTo: "",
            sendToCc: "",
            sendToBcc: "",
            subjectFor: "",
          }
        )
      );
      localStorage.setItem("emailTemplate", JSON.stringify(metadataData || []));
      window.location.reload();
    } catch (error) {
      console.error("Error loading template:", error);
      handleErrorMessage("Failed to load template");
    }
  };

  useEffect(() => {
      const data = localStorage.getItem("column_data")
      const data1 = JSON.parse(data)
      if (data1.query) {
        setQuery(data1.query);
      }
    }, []);

  return (
    // <div className="bg-gradient-to-r from-blue-400 via-blue-300 to-orange-400 shadow-lg">
    <div className="bg-blue-400 shadow-lg">
      <TestEmailPopUp
        open={open}
        loading={loading}
        onClose={handleClose}
        onSend={sendEmail}
      />

      {/* Top bar with branding and actions */}
      <div className="px-6 py-4 flex justify-between items-center">
        {/* Left: Branding */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-9 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
            <span className="text-white font-bold">EB</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white drop-shadow-md">
              Email Builder
            </h1>
            <p className="text-xs text-white/80 font-medium">
              Design • Preview • Deploy
            </p>
          </div>
        </div>

        {/* Center: Device Preview Toggle */}
        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-xl p-1.5 border border-white/20">
          {/* <button onClick={()=>navigate(-1)}>back</button> */}
          <Button
            variant={screenSize === "desktop" ? "contained" : "text"}
            size="small"
            onClick={() => setScreenSize("desktop")}
            sx={{
              minWidth: "auto",
              px: 2,
              color: screenSize === "desktop" ? "#1e293b" : "white",
              backgroundColor:
                screenSize === "desktop" ? "white" : "transparent",
              "&:hover": {
                backgroundColor:
                  screenSize === "desktop"
                    ? "#f1f5f9"
                    : "rgba(255,255,255,0.1)",
              },
            }}
          >
            <Monitor className="w-4 h-4" />
            {/* <span className="ml-1 text-xs font-medium">Desktop</span> */}
          </Button>
          <Button
            variant={screenSize === "mobile" ? "contained" : "text"}
            size="small"
            onClick={() => setScreenSize("mobile")}
            sx={{
              minWidth: "auto",
              px: 2,
              color: screenSize === "mobile" ? "#1e293b" : "white",
              backgroundColor:
                screenSize === "mobile" ? "white" : "transparent",
              "&:hover": {
                backgroundColor:
                  screenSize === "mobile" ? "#f1f5f9" : "rgba(255,255,255,0.1)",
              },
            }}
          >
            <Smartphone className="w-4 h-4" />
            {/* <span className="ml-1 text-xs font-medium">Mobile</span> */}
          </Button>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-2">
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectedElement(null);
              setEditDiv(false);
              viewHTMLCode(true);
              setCopy(true);
            }}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              color: "white",
              borderColor: "rgba(255,255,255,0.3)",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.6)",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            <Code className="w-4 h-4" />
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSelectedElement(null);
              setEditDiv(false);
              setTestEmail(true);
              setOpen(true);
            }}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              color: "white",
              borderColor: "rgba(255,255,255,0.3)",
              "&:hover": {
                borderColor: "rgba(255,255,255,0.6)",
                backgroundColor: "rgba(255,255,255,0.1)",
              },
            }}
          >
            Test Email
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setSelectedElement(null);
              setEditDiv(false);
              setShowTemp(true);
            }}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              backgroundColor: "white",
              color: "#6366f1",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Templates
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setSelectedElement(null);
              setEditDiv(false);
              // setTimeout(()=>handleSaveTemplate(), 500);
              setShowSaveDialog(true);
            }}
            sx={{
              textTransform: "none",
              borderRadius: 3,
              backgroundColor: "white",
              color: "#6366f1",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Save Template
          </Button>

          <TemplatesDialog
            open={showTemp}
            onClose={() => setShowTemp(false)}
            onSelectTemplate={handleSelectTemplate}
          />
          <SaveOptionsDialog
            open={showSaveDialog}
            onClose={() => setShowSaveDialog(false)}
            onSave={handleSaveTemplate}
            loading={loading}
            setLoading={setLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default EmailEditorHeader;
